// Intranet-side AI assistant wiring for the shared AppAssistant component.
//
// The shared AppAssistant (workspace/packages/shared/components/AppAssistant.js)
// calls these via `el.call('...')`:
//
//   - aiStream(messages, attachment, onChunk, onDone)
//       Streams the AI response to the governance ai-chat edge function
//       and burns credits via the shared credit-meter.
//
//   - aiPerformAction(action, s)
//       Dispatches a confirmed action proposed by the assistant
//       (create_ticket, create_event, create_note, create_kb,
//        update_ticket, move_ticket).
//
//   - aiActionStyles()
//       Returns the icon/color/label map used for proposed-action cards.
//
//   - aiSuggestions()
//       Returns the starter prompts shown in the Welcome view.
//
//   - aiWelcomeText()
//       Returns the Welcome copy shown above the suggestions.
//
//   - fetchPendingApprovals()  — TASK#4360
//       Returns tickets in state='awaiting_approval' for the governance
//       workspace. Server-side, the ai-chat edge function injects the same
//       data into the AI prompt; this client helper exists so non-AI
//       surfaces (or future inline cards) can surface the count/list with
//       the same query and avoid drift.
//
// Canvas registers nothing; the toggle button self-hides when
// `context.functions.aiStream` is absent.
//
//   - subscribeTicketRealtime(root)
//       Subscribes to ticket updates over the SDK realtime channel. On state
//       transitions (→done, blocked→in_progress, →awaiting_approval) for
//       tickets whose metadata.simona_user_id matches the signed-in user,
//       injects a system message into root.assistantMessages so Simona
//       auto-surfaces the update in the thread.
//
//   - unsubscribeTicketRealtime()
//       Removes the realtime channel opened by subscribeTicketRealtime.

// migrated 2026-04-27 — getSupabase + sb.channel/removeChannel replaced by
// the SDK realtime API (`workspaceProject.realtime.tickets` subscribe).
// migrated 2026-05-20 — raw Supabase ai-chat fetch replaced by SDK
// `sdk.aiChat.stream` (Mongo-backed, server holds the provider keys).
// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
// Same page snapshot simoneAiTransport dispatches — one builder, so the two
// entry points can't drift into telling the model different things about the
// page the user is looking at.
import { buildPageContext } from './pageContext.js'

// Credit metering for ai-chat now happens server-side inside the
// AiChatService — server knows the actual token count from the AI
// provider response, so client-side estimation is inherently
// inaccurate AND trivially manipulable from the browser.
// See server/packages/credit-meter for the canonical burn primitive.

// Builds a workspace-context prefix appended to the server's system prompt
// so the AI knows which Symbols app surface the user is on.
// `el` — the DOMQL element that called aiStream (via `el.call('aiStream', ...)`).
// Returns a string to inject, or null when context is unavailable.
const _buildAppContext = (el) => {
  try {
    const root = el?.state?.root || el?.context?.state
    const path =
      (typeof window !== 'undefined' ? window.location.pathname : null) || root?.currentPath || '/'
    const user = root?.currentUser
    const workspace = root?.activeWorkspace
    const orgName = workspace?.name || workspace?.title || null
    const lines = [
      'CURRENT APP CONTEXT:',
      `  Surface: Symbols Workspace intranet (workspace.localhost / workspace.symbols.app)`,
      `  Current route: ${path}`
    ]
    if (orgName) lines.push(`  Active workspace: ${orgName}`)
    if (user?.name) lines.push(`  Signed-in user: ${user.name} (${user.email || ''})`.trim())
    lines.push(
      '',
      'You have knowledge of the Symbols workspace app features: calendar, chat, tickets, documents, people directory, announcements, company pages, and data. Answer questions about any of these surfaces using the context above.'
    )
    return lines.join('\n')
  } catch (_) {
    return null
  }
}

// Pull the text to send out of the caller's message list. `sdk.ai.stream`
// resolves ONE turn against a server-side conversation, which is where the
// history lives — the array is the caller's local view of it, and only its
// newest user turn is new information.
const _latestUserText = (messages) => {
  const list = Array.isArray(messages) ? messages : []
  for (let i = list.length - 1; i >= 0; i--) {
    const m = list[i]
    if (!m || (m.role && m.role !== 'user')) continue
    const t = typeof m === 'string' ? m : m.content || m.text || ''
    if (typeof t === 'string' && t.trim()) return t.trim()
  }
  // A bare string, or a list of plain strings.
  if (typeof messages === 'string' && messages.trim()) return messages.trim()
  return ''
}

// Streams an AI turn through the unified `sdk.ai.stream` surface.
//
// onChunk receives the FULL accumulated text after each delta (batched
// per-tick so reactive consumers don't re-render per token); onDone
// receives the final text.
// `this` is the calling DOMQL element when invoked via el.call() — used
// to read route + workspace context for the system prompt injection (#2270).
//
// `opts.conversationId` continues a specific thread. WITHOUT it every call
// opens a NEW conversation — see below for why that is the only safe default.
//
// Three defects fixed here on 2026-08-08, all of which produced answers about
// something else entirely:
//
//   1. This passed `{ messages }`, but the SDK's workspace-turn transport
//      reads `payload.content || payload.text` — so the POSTed user message
//      was the EMPTY STRING on every call. The model was asked nothing.
//   2. With no `conversationId`, the SDK falls back to the per-workspace
//      CACHED conversation. Combined with (1) that meant "append an empty
//      message to whatever thread this workspace last used, and let the model
//      continue it" — which is exactly what was observed: asking "which of my
//      files is a spreadsheet" returned prose about a "Blog Post Manager"
//      extension from an unrelated earlier thread, and three follow-ups came
//      back as raw tool-call JSON rendered as the answer text.
//   3. `appContext` is not a key the transport forwards; it reads `context`.
//      The route/workspace preamble built above was silently dropped, and the
//      page snapshot was never sent at all.
export const aiStream = async function aiStream(messages, attachment, onChunk, onDone, opts = {}) {
  let full = ''
  let settled = false
  // The client's own replies are bundle sentences. `this` is the element
  // (`el.call('aiStream', …)`); a direct call has none and gets the key.
  const say = (key) => (this && typeof this.call === 'function' ? this.call('polyglot', key) : key)
  const finish = (text) => {
    if (settled) return
    settled = true
    try {
      onDone(text)
    } catch (_) {
      /* swallow */
    }
  }
  const content = _latestUserText(messages)
  if (!content) {
    // Never post an empty turn — that is precisely how this resurrected a
    // stale thread and answered from it.
    console.warn('[aiAssistant] aiStream called with no user text — nothing sent.')
    finish('')
    return
  }
  try {
    const appCtx = _buildAppContext(this)
    const root = this?.state?.root || this?.context?.state
    // Same per-turn context shape simoneAiTransport dispatches, so the server
    // surfaces it in the agent's system instruction the same way.
    let pageCtx = null
    try {
      const pc = root ? buildPageContext(root) : null
      if (pc && pc.currentPage) {
        pageCtx = {
          currentPage: pc.currentPage,
          pageData: pc.pageData || null,
          pageActions: pc.pageActions || null,
          openView: pc.openView || null,
          uiState: pc.uiState || null
        }
      }
    } catch (_) {
      /* page awareness is best-effort — never block a turn on it */
    }
    const ai = await shellGetSDK().getService('ai')
    ai.stream(
      {
        content,
        attachedCard: attachment || null,
        // Continue the caller's thread when it names one; otherwise START one.
        // Inheriting the workspace's cached thread is never right for a helper
        // that holds no thread of its own — it answers from someone else's
        // conversation.
        conversationId: opts.conversationId || null,
        newConversation: !opts.conversationId,
        context: {
          surface: 'workspace-shell',
          ...(appCtx ? { appContext: appCtx } : {}),
          ...(pageCtx || {})
        }
      },
      {
        onChunk: (delta) => {
          full += delta
          try {
            onChunk(full)
          } catch (_) {
            /* swallow */
          }
        },
        onDone: (payload) => {
          const finalText = payload?.text || full || say('workspace.ai.reply.empty')
          finish(finalText)
        },
        onError: (err) => {
          console.error('[aiAssistant] aiStream error:', err)
          finish(say('workspace.ai.reply.connectionError'))
        }
      }
    )
  } catch (e) {
    console.error('[aiAssistant] AI stream failed:', e)
    finish(say('workspace.ai.reply.connectionError'))
  }
}

export const aiActionStyles = function aiActionStyles() {
  return {
    create_ticket: { icon: 'check', color: 'orange', label: 'workspace.ai.action.createTicket' },
    create_event: { icon: 'date outline', color: 'blue', label: 'workspace.ai.action.createEvent' },
    create_note: { icon: 'document', color: 'green', label: 'workspace.ai.action.createNote' },
    create_kb: { icon: 'document', color: 'cerulean', label: 'workspace.ai.action.createKb' },
    update_ticket: { icon: 'edit', color: 'yellow', label: 'workspace.ai.action.updateTicket' },
    move_ticket: { icon: 'arrowRight', color: 'purple', label: 'workspace.ai.action.moveTicket' }
  }
}

export const aiSuggestions = function aiSuggestions() {
  return [
    { text: this.call('polyglot', 'workspace.ai.suggest.team') },
    { text: this.call('polyglot', 'workspace.ai.suggest.events') },
    { text: this.call('polyglot', 'workspace.ai.suggest.documents') },
    { text: this.call('polyglot', 'workspace.ai.suggest.tasks') },
    { text: this.call('polyglot', 'workspace.ai.suggest.approvals') }
  ]
}

// fetchPendingApprovals — TASK#4360
// Note: the previous implementation hard-coded the governance workspace_id
// (69e5911201b0ef47b675463f) into the REST query so unauthenticated/anon
// callers could still pull governance approvals. After migrating to the
// SDK, scoping comes from the caller's JWT (active_workspace_id claim) —
// which is correct multi-tenant behavior. Anon callers can no longer
// query approvals; they need to authenticate first.
//
// Returns the list of tickets currently in state='awaiting_approval' for the
// governance workspace. These tickets are blocked from agents until a human
// approves them via the /tickets/general page (where the Approve action
// transitions state → in_progress).
//
// The server-side `ai-chat` edge function injects the same query result into
// the AI's prompt context under "PENDING APPROVALS" so Simona can surface
// them naturally when asked. This client helper exists for parity — non-AI
// surfaces (counters, badges, future inline cards) can call into it via
// `el.call('fetchPendingApprovals')` and stay in sync with the server.
//
// Returns: Promise<Array<{
//   id, number, title, priority, labels, assignee_email, reporter_email,
//   updated_at, created_at, ageMs, needsHumanApproval
// }>> — sorted newest-first. Empty array on error or no rows.
export const fetchPendingApprovals = async function fetchPendingApprovals() {
  try {
    // Phase 4: sdk.tickets.list (Mongo-backed main server).
    const tSvc = await shellGetSDK().getService('tickets')
    const result = await tSvc.list(
      { state: 'awaiting_approval', deleted: false },
      { limit: 50, sortBy: 'updatedAt', sortDir: 'desc' }
    )
    const rows = Array.isArray(result?.data) ? result.data : result?.data || result || []
    if (!Array.isArray(rows)) return []
    const now = Date.now()
    return rows.map((t) => {
      const labels = Array.isArray(t.labels) ? t.labels : []
      const ts = t.updated_at || t.created_at
      const ageMs = ts ? now - new Date(ts).getTime() : null
      return {
        ...t,
        labels,
        ageMs: Number.isFinite(ageMs) && ageMs >= 0 ? ageMs : null,
        needsHumanApproval: labels.includes('human-approval-needed')
      }
    })
  } catch (err) {
    console.error('[aiAssistant] fetchPendingApprovals error:', err)
    return []
  }
}

export const aiWelcomeText = function aiWelcomeText() {
  return this.call('polyglot', 'workspace.ai.emptyHint')
}

// Action → [registered function name, payload builder]. `me` is the
// current user's email. Each builder returns the payload to pass into
// the named function via `el.call`.
// `t` is a translate function the caller passes in (`el.call('polyglot', k)`);
// each builder stays self-contained over its parameters, so frank.toJSON can
// revive it without a closure over module scope.
const _ACTION_DISPATCH = {
  create_ticket: (a, me, t) => [
    'saveTicket',
    {
      title: a.title || t('workspace.ai.newRecord.ticket'),
      body: a.body || '',
      columnKey: 'backlog',
      priority: a.priority || 'medium',
      assigneeEmail: a.assignee || me,
      reporterEmail: me
    }
  ],
  create_event: (a, me, t) => [
    'saveEvent',
    {
      title: a.title || t('workspace.ai.newRecord.event'),
      date: a.date || new Date().toISOString().slice(0, 10),
      startTime: a.startTime || '09:00',
      endTime: a.endTime || '10:00',
      type: 'meeting',
      creatorEmail: me
    }
  ],
  create_note: (a, me, t) => [
    'createNote',
    {
      title: a.title || t('workspace.ai.newRecord.note'),
      content: a.content || a.body || '',
      source: 'ai'
    }
  ],
  create_kb: (a, me, t) => [
    'saveKbArticle',
    {
      title: a.title || t('workspace.ai.newRecord.article'),
      category: a.category || t('workspace.ai.newRecord.category'),
      content: a.content || a.body || '',
      summary: (a.content || a.body || '').slice(0, 100)
    }
  ],
  move_ticket: (a) => [
    'moveTicket',
    {
      ticketId: a.number,
      columnKey: a.column_key,
      position: 0
    }
  ]
}

export const aiPerformAction = function aiPerformAction(a, s) {
  if (!a) return
  const root = (s && s.root) || this?.state?.root
  const me = root?.currentUserEmail
  const type = a.type

  // `update_ticket` writes directly through the SDK rather than through a
  // registered mutator, so it stays a special case outside the table.
  if (type === 'update_ticket') {
    const row = { updated_at: new Date().toISOString() }
    if (a.body) row.body = a.body
    if (a.title) row.title = a.title
    if (a.column_key) row.column_key = a.column_key
    if (a.priority) row.priority = a.priority
    // Phase 4: sdk.tickets.update(id, payload) — getService is Proxy-deferred
    Promise.resolve(shellGetSDK().getService('tickets'))
      .then((svc) => svc.update(a.number, row))
      .catch((err) => console.error('[aiAssistant] update_ticket failed:', err))
    return
  }

  const builder = _ACTION_DISPATCH[type]
  if (!builder) return
  const [fnName, payload] = builder(a, me, (k) => this.call('polyglot', k))
  this.call(fnName, payload)
}

// ─── Realtime ticket subscription ──────────────────────────────────────────
//
// Watches ticket updates via the SDK realtime channel. When a Simona-originated
// ticket (metadata.simona_user_id === current user) transitions to a
// noteworthy state, injects a system notification card into the assistant
// thread so Simona surfaces it without being invoked manually.
//
// Design invariants:
//   - One channel per session; calling subscribeTicketRealtime again
//     while already subscribed is a no-op (returns existing channel).
//   - User identity comes from root.currentUserId (preferred) with
//     root.currentUserEmail as fallback — matches how simona.md tags tickets.
//   - System messages use role:'system' so AppAssistantMessage renders them
//     with the 'AI' role label and the standard assistant bubble style.
//   - unsubscribeTicketRealtime() removes the channel and clears the ref so
//     the AppAssistant onRemove lifecycle can clean up without leaking sockets.

// Noteworthy state transitions → the sentence the thread shows. One whole
// sentence per state: a state word slotted into one English frame cannot be
// translated. Every key is written out, so grep and the key gates see it.
const _TRANSITION_KEY = {
  done: 'workspace.assistant.ticketNotice.done',
  awaiting_approval: 'workspace.assistant.ticketNotice.awaitingApproval',
  in_progress: 'workspace.assistant.ticketNotice.started'
}

// Build the notification text for a ticket state change. `say(key)` resolves
// a bundle key. Each value goes in through a replacer FUNCTION, and the ticket
// subject goes in LAST, so a `$&` or a `{commit}` inside a title stays text.
const _notifyText = (say, ticket, newState) => {
  const sha = ticket.metadata?.commitSha
  const commit = sha
    ? say('workspace.assistant.ticketNotice.commit').replace('{sha}', () => sha.slice(0, 7))
    : ''
  const subject = ticket.title
    ? say('workspace.assistant.ticketNotice.titled').replace('{title}', () => ticket.title)
    : say('workspace.assistant.ticketNotice.numbered').replace('{id}', () => String(ticket.id))
  // Intranet ticket deep-link — relative path works for all origins.
  return say(_TRANSITION_KEY[newState])
    .replace('{commit}', () => commit)
    .replace('{href}', () => `/tickets/${ticket.id}`)
    .replace('{ticket}', () => subject)
}

// Module-level subscription handle so subscribe/unsubscribe are stateless
// from the component's perspective — it just calls el.call('subscribeTicketRealtime', root).
//
// STALE-COMMENT FIX (2026-07-27): this used to say ticket realtime rode
// `workspaceProject.realtime.tickets` via a legacy realtime provider
// in bootShell. Neither claim matches the code
// below anymore — Phase 4 replaced that path with the SDK's dedicated
// `TicketService` SSE stream (`getSDK().getService('tickets').subscribe()`,
// see the inline comment at the call site), which is NOT routed through
// `sdkRealtimeProvider`/`workspaceProject.realtime.*` at all (that provider
// only ever handles `meet` / `presence` / `notifications` — anything else,
// including `tickets`, has no backend there, same as chat). Mirrors the
// pattern the chat page's own `subscribeRealtime.js` uses: tickets realtime
// is owned by the tickets SSE service, not this generic provider.
// WS-SWITCH-MEMO-PLANE-1 — the workspace id the stream belongs to. `/tickets/
// stream` is WORKSPACE-scoped: the SDK binds the active workspace at open
// time. Both latches below are module-level memos that survive an in-app
// switch, so their reuse key MUST carry the workspace, or workspace B keeps
// reading workspace A's stream until a full reload (B's own events never
// arrive; A's keep landing in B's root). An unresolved workspace keys as ''
// — a DIFFERENT scope, so it re-opens once the id lands: a miss, never a
// stale serve. Tenant-keyed, not torn down on switch, per the ticket's rule.
const _streamWsId = (root) => {
  const ws = root?.workspace
  return String((ws && (ws.id || ws._id)) || '')
}

let _ticketUnsub = null
let _ticketChannelKey = null // `${userId}|${wsId}` the channel was opened for

export const subscribeTicketRealtime = function subscribeTicketRealtime(root) {
  // `el.call('subscribeTicketRealtime', root)` binds the element, whose
  // polyglot speaks the workspace's language. A bare call has no element and
  // reads the shipped English — never a raw key.
  const el = this
  const say = (key) =>
    el && typeof el.call === 'function'
      ? String(el.call('polyglot', key))
      : String(_wsShared.functions.i18nStaticEnglish()[key] || key)
  const userId = root?.currentUserId || root?.currentUserEmail || null
  const wsId = _streamWsId(root)
  const channelKey = `${userId || ''}|${wsId}`

  // If already subscribed for the same user IN THE SAME WORKSPACE, skip.
  if (_ticketUnsub && _ticketChannelKey === channelKey) return _ticketUnsub

  // If subscribed for a DIFFERENT identity or workspace (workspace switch /
  // sign-out + re-in), tear down and re-subscribe for the new scope.
  if (_ticketUnsub && _ticketChannelKey !== channelKey) {
    unsubscribeTicketRealtime()
  }

  // No user yet — defer; subscribeTicketRealtime will be called again once
  // root.currentUserId populates (AppAssistant.Panel.Thread.onUpdate retries).
  if (!userId) return null

  _ticketChannelKey = channelKey

  // Phase 4: sdk.tickets.subscribe (SSE) replaces wp.realtime.subscribeTickets.
  // getSDK() returns a Proxy — getService() is deferred via initPromise so we
  // must await it (Promise.resolve pattern to stay sync-callable).
  let _resolved = null
  let _cancelled = false
  Promise.resolve(shellGetSDK().getService('tickets'))
    .then((ticketService) => {
      if (_cancelled) return
      _resolved = ticketService.subscribe(wsId ? { workspaceId: wsId } : {}, (eventObj) => {
        if (eventObj?.type !== 'tickets.update') return
        // Late-write guard: an event from the workspace this stream was opened
        // for must not land in a root that has since switched tenant.
        if (_streamWsId(root) !== wsId) return
        const ticket = eventObj?.ticket
        if (!ticket) return

        // Filter: only tickets Simona tagged for this user.
        const meta = ticket.metadata || {}
        const ticketUserId = meta.simona_user_id
        if (!ticketUserId || ticketUserId !== userId) return

        const newState = ticket.state
        if (!_TRANSITION_KEY[newState]) return

        const notifMsg = {
          role: 'system',
          content: _notifyText(say, ticket, newState),
          _ticketNotif: true,
          _ticketId: ticket.id,
          _ticketState: newState
        }
        const current = root?.assistantMessages || []
        if (root?.update) {
          root.update({
            assistantMessages: [...current, notifMsg],
            assistantOpen: true
          })
        }
      })
    })
    .catch((err) => {
      console.warn('[aiAssistant] subscribeTicketRealtime failed:', err?.message || err)
    })

  // Stable unsubscribe wrapper.
  _ticketUnsub = () => {
    _cancelled = true
    if (typeof _resolved === 'function') {
      try {
        _resolved()
      } catch (_) {}
    }
  }
  return _ticketUnsub
}

export const unsubscribeTicketRealtime = function unsubscribeTicketRealtime() {
  if (!_ticketUnsub) return
  // Read the latch into a local and call THAT: frank lifts this module `let`
  // into its state bag, but a bare call of the binding shipped unrewritten.
  const unsub = _ticketUnsub
  _ticketUnsub = null
  _ticketChannelKey = null
  try {
    unsub()
  } catch (_) {}
}

// ─── ASK-USER decision panel subscription ──────────────────────────────────
//
// Watches public.tickets for INSERT/UPDATE events where:
//   - assignee_email contains 'simona', OR
//   - labels includes 'ASK-USER' or 'ask-user' or 'human-approval-needed'
// AND ticket type='decision' (TEMO's open_resolution_ticket + ASK-USER label).
//
// When a qualifying ticket lands, splices it into root.askUserDecisions so
// the AppAssistantDecisionBanner in the panel reacts immediately.
// Dismissed tickets are tracked in a module-level Set so they don't reappear
// after a realtime UPDATE (e.g. state change fires another event).
//
// Design invariants:
//   - One subscription per session; idempotent on re-call.
//   - Dismissed cards (stored in root._dismissedDecisionIds) never reappear
//     unless the page refreshes (Set lives in memory, not localStorage).
//   - subscribeAskUserDecisions and unsubscribeAskUserDecisions mirror the
//     subscribeTicketRealtime / unsubscribeTicketRealtime pattern so
//     AppAssistant.onInit/onRemove can hook in consistently.

const _isAskUserTicket = (ticket) => {
  if (!ticket) return false
  const labels = Array.isArray(ticket.labels) ? ticket.labels : []
  const assignee = (ticket.assignee_email || '').toLowerCase()
  return (
    labels.some((l) => ['ASK-USER', 'ask-user', 'human-approval-needed'].includes(l)) ||
    assignee.includes('simona')
  )
}

const _isResolvedDecision = (ticket) => {
  // Cards where the human already responded — hide from the panel.
  const state = ticket?.state
  return state === 'done' || state === 'cancelled' || state === 'in_progress'
}

// Active subscription handle + the workspace it was opened for (see
// `_streamWsId` above — same memo-plane rule: the reuse key carries the tenant).
let _askUserUnsub = null
let _askUserScope = null

export const subscribeAskUserDecisions = function subscribeAskUserDecisions(root) {
  const wsId = _streamWsId(root)
  if (_askUserUnsub && _askUserScope === wsId) return _askUserUnsub
  // A stream bound to ANOTHER workspace: close it and open this tenant's own.
  if (_askUserUnsub) unsubscribeAskUserDecisions()
  _askUserScope = wsId

  // Phase 4: sdk.tickets.subscribe (SSE) replaces wp.realtime.subscribeTickets.
  // getSDK() Proxy defers getService — await via Promise.resolve.
  let _resolved = null
  let _subscCancelled = false
  Promise.resolve(shellGetSDK().getService('tickets'))
    .then((ticketService) => {
      if (_subscCancelled) return
      _resolved = ticketService.subscribe(wsId ? { workspaceId: wsId } : {}, (eventObj) => {
        const { type, ticket } = eventObj || {}
        if (type !== 'tickets.insert' && type !== 'tickets.update') return
        if (!ticket) return
        // Late-write guard: never merge this workspace's rows into a root that
        // has since switched tenant.
        if (_streamWsId(root) !== wsId) return

        // Only process ASK-USER tickets.
        if (!_isAskUserTicket(ticket)) return

        const dismissed = root?._dismissedDecisionIds || []
        const isDismissed = dismissed.includes(ticket.id)

        if (_isResolvedDecision(ticket) || isDismissed) {
          const existing = root?.askUserDecisions || []
          if (existing.some((d) => d.id === ticket.id)) {
            root.update({
              askUserDecisions: existing.filter((d) => d.id !== ticket.id)
            })
          }
          return
        }

        const current = root?.askUserDecisions || []
        const idx = current.findIndex((d) => d.id === ticket.id)
        const updated =
          idx >= 0
            ? current.map((d, i) => (i === idx ? { ...d, ...ticket } : d))
            : [ticket, ...current]

        if (root?.update) {
          root.update({ askUserDecisions: updated, assistantOpen: true })
        }
      })
    })
    .catch((err) => {
      console.warn('[aiAssistant] subscribeAskUserDecisions failed:', err?.message || err)
    })

  _askUserUnsub = () => {
    _subscCancelled = true
    if (typeof _resolved === 'function') {
      try {
        _resolved()
      } catch (_) {}
    }
  }
  return _askUserUnsub
}

export const unsubscribeAskUserDecisions = function unsubscribeAskUserDecisions() {
  if (!_askUserUnsub) return
  // Read the latch into a local and call THAT (see unsubscribeTicketRealtime).
  const unsub = _askUserUnsub
  _askUserUnsub = null
  _askUserScope = null
  try {
    unsub()
  } catch (_) {}
}

// ─── fetchAskUserDecisions ──────────────────────────────────────────────────
//
// Fetches all open ASK-USER / awaiting-approval / human-approval-needed
// tickets that require a decision from the signed-in user (Nika). Used by
// AppAssistant.onInit to seed root.askUserDecisions before realtime takes over.

export const fetchAskUserDecisions = async function fetchAskUserDecisions(root) {
  try {
    // Phase 4: sdk.tickets.list (Mongo-backed main server).
    const tSvc = await shellGetSDK().getService('tickets')
    const result = await tSvc.list(
      { deleted: false },
      { limit: 100, sortBy: 'updatedAt', sortDir: 'desc' }
    )
    const rows = Array.isArray(result?.data) ? result.data : result?.data || result || []
    if (!Array.isArray(rows)) return []

    const dismissed = root?._dismissedDecisionIds || []
    return rows.filter(
      (t) => _isAskUserTicket(t) && !_isResolvedDecision(t) && !dismissed.includes(t.id)
    )
  } catch (err) {
    console.error('[aiAssistant] fetchAskUserDecisions error:', err)
    return []
  }
}

// ─── approveDecisionTicket ──────────────────────────────────────────────────
//
// PATCH ticket state='in_progress' + metadata.nika_decision='approved'.
// The `options` field in metadata is preserved; existing metadata is merged.
// Removes 'ASK-USER' / 'ask-user' / 'human-approval-needed' labels and adds
// 'human-approval-given'. Logs an activity entry with kind='human_approved'.

export const approveDecisionTicket = async function approveDecisionTicket(ticketId, root) {
  if (!ticketId) return
  try {
    // Read current ticket from local cache first.
    const decisions = root?.askUserDecisions || []
    const ticket = decisions.find((d) => d.id === ticketId)
    const currentLabels = Array.isArray(ticket?.labels) ? ticket.labels : []
    const currentMeta = ticket?.metadata || {}

    const newLabels = [
      ...currentLabels.filter(
        (l) => !['ASK-USER', 'ask-user', 'human-approval-needed'].includes(l)
      ),
      'human-approval-given'
    ]

    // Phase 4: sdk.tickets.update(id, payload) — getService is Proxy-deferred
    const tSvc1 = await shellGetSDK().getService('tickets')
    await tSvc1.update(ticketId, {
      state: 'in_progress',
      labels: newLabels,
      metadata: {
        ...currentMeta,
        nika_decision: 'approved',
        nika_decided_at: new Date().toISOString()
      },
      updated_at: new Date().toISOString()
    })

    // Dismiss the card immediately — don't wait for realtime echo.
    const dismissed = [...(root?._dismissedDecisionIds || []), ticketId]
    root.update({
      askUserDecisions: (root?.askUserDecisions || []).filter((d) => d.id !== ticketId),
      _dismissedDecisionIds: dismissed
    })
  } catch (err) {
    console.error('[aiAssistant] approveDecisionTicket error:', err)
  }
}

// ─── rejectDecisionTicket ───────────────────────────────────────────────────
//
// PATCH ticket state='cancelled' with metadata.nika_decision='rejected' +
// metadata.nika_rejection_reason (the user's reason text).
// Removes ASK-USER labels, adds 'human-approval-rejected'.

export const rejectDecisionTicket = async function rejectDecisionTicket(ticketId, reason, root) {
  if (!ticketId) return
  try {
    const decisions = root?.askUserDecisions || []
    const ticket = decisions.find((d) => d.id === ticketId)
    const currentLabels = Array.isArray(ticket?.labels) ? ticket.labels : []
    const currentMeta = ticket?.metadata || {}

    const newLabels = [
      ...currentLabels.filter(
        (l) => !['ASK-USER', 'ask-user', 'human-approval-needed'].includes(l)
      ),
      'human-approval-rejected'
    ]

    // Phase 4: sdk.tickets.update(id, payload) — getService is Proxy-deferred
    const tSvc2 = await shellGetSDK().getService('tickets')
    await tSvc2.update(ticketId, {
      state: 'cancelled',
      labels: newLabels,
      metadata: {
        ...currentMeta,
        nika_decision: 'rejected',
        nika_rejection_reason: reason || '',
        nika_decided_at: new Date().toISOString()
      },
      updated_at: new Date().toISOString()
    })

    const dismissed = [...(root?._dismissedDecisionIds || []), ticketId]
    root.update({
      askUserDecisions: (root?.askUserDecisions || []).filter((d) => d.id !== ticketId),
      _dismissedDecisionIds: dismissed
    })
  } catch (err) {
    console.error('[aiAssistant] rejectDecisionTicket error:', err)
  }
}

// ─── clarifyDecisionTicket ──────────────────────────────────────────────────
//
// PATCH ticket metadata.clarification with the user's clarification text.
// Leaves ticket in state='awaiting_approval' (agent reads the clarification
// and proceeds). Removes the card from the panel after submit.

export const clarifyDecisionTicket = async function clarifyDecisionTicket(
  ticketId,
  clarification,
  root
) {
  if (!ticketId || !clarification) return
  try {
    const decisions = root?.askUserDecisions || []
    const ticket = decisions.find((d) => d.id === ticketId)
    const currentMeta = ticket?.metadata || {}

    // Phase 4: sdk.tickets.update(id, payload) — getService is Proxy-deferred
    const tSvc3 = await shellGetSDK().getService('tickets')
    await tSvc3.update(ticketId, {
      metadata: {
        ...currentMeta,
        clarification,
        clarification_at: new Date().toISOString()
      },
      updated_at: new Date().toISOString()
    })

    const dismissed = [...(root?._dismissedDecisionIds || []), ticketId]
    root.update({
      askUserDecisions: (root?.askUserDecisions || []).filter((d) => d.id !== ticketId),
      _dismissedDecisionIds: dismissed
    })
  } catch (err) {
    console.error('[aiAssistant] clarifyDecisionTicket error:', err)
  }
}
